/**
 * useStudioTemplates.ts — Hooks de dados do Studio
 *
 * Compõe sobre a camada `src/lib/studio/api.ts` (somente RPCs) e mantém o cache
 * do TanStack Query coerente após gravação e publicação.
 *
 * RELEASE: v1.2.0 - 2026-09-10 - Envios de formulário das páginas públicas
 * RELEASE: v1.1.0 - 2026-09-10 - Publicação de página pública (landing page)
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 1)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTemplate,
  listPageSubmissions,
  listTemplates,
  publishPage,
  publishVersion,
  upsertDraft,
  type StudioDraftInput,
  type StudioPublishPageInput,
  type StudioSubmissionFilters,
  type StudioTemplateFilters,
} from '@/lib/studio/api';

/** Chaves de cache do módulo. */
export const studioKeys = {
  all: ['studio'] as const,
  list: (filtros: StudioTemplateFilters) => ['studio', 'templates', filtros] as const,
  detail: (id: string) => ['studio', 'template', id] as const,
};

/** Lista os modelos da empresa com filtros. */
export function useStudioTemplates(filtros: StudioTemplateFilters = {}) {
  return useQuery({
    queryKey: studioKeys.list(filtros),
    queryFn: () => listTemplates(filtros),
    staleTime: 30_000,
  });
}

/** Abre um modelo específico. */
export function useStudioTemplate(templateId: string | null) {
  return useQuery({
    queryKey: studioKeys.detail(templateId ?? ''),
    queryFn: () => getTemplate(templateId as string),
    enabled: Boolean(templateId),
  });
}

/** Salva a versão em rascunho de um modelo. */
export function useSaveStudioDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entrada: StudioDraftInput) => upsertDraft(entrada),
    onSuccess: (resultado) => {
      queryClient.invalidateQueries({ queryKey: studioKeys.all });
      queryClient.invalidateQueries({ queryKey: studioKeys.detail(resultado.template_id) });
    },
  });
}

/** Publica a versão em rascunho de um modelo. */
export function usePublishStudioTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, versionId }: { templateId: string; versionId?: string | null }) =>
      publishVersion(templateId, versionId),
    onSuccess: (resultado) => {
      queryClient.invalidateQueries({ queryKey: studioKeys.all });
      queryClient.invalidateQueries({ queryKey: studioKeys.detail(resultado.template_id) });
    },
  });
}

/**
 * Publica uma página pública (landing page).
 *
 * A compilação e o saneamento acontecem no servidor; aqui apenas enviamos o
 * endereço e os dados de SEO e atualizamos o cache.
 */
export function usePublishStudioPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entrada: StudioPublishPageInput) => publishPage(entrada),
    onSuccess: (resultado) => {
      queryClient.invalidateQueries({ queryKey: studioKeys.all });
      queryClient.invalidateQueries({ queryKey: studioKeys.detail(resultado.template_id) });
    },
  });
}

/**
 * Lista os envios de formulário de uma página pública (Studio, Fase 4).
 * Mantém a página anterior enquanto busca a próxima, evitando piscar a tabela.
 */
export function useStudioPageSubmissions(
  filtros: StudioSubmissionFilters,
  habilitado = true,
) {
  return useQuery({
    queryKey: [...studioKeys.all, 'submissions', filtros] as const,
    queryFn: () => listPageSubmissions(filtros),
    enabled: habilitado && Boolean(filtros.templateId),
    placeholderData: (anterior) => anterior,
    staleTime: 15_000,
  });
}
